import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useEffect } from "react";
import { ArrowRight, Check, MessageSquareQuote, Minus } from "lucide-react";

import { buildSeo } from "@/lib/seo";
import { MarketingLayout, Eyebrow } from "@/components/marketing/Layout";
import { getTopicDe, TOPICS_DE } from "@/lib/topics.de";
import { setLanguage } from "@/i18n";

const SITE = "https://realitytwin.io";

/**
 * German topic page.
 *
 * Renders German from static data with no runtime translation, so the HTML is
 * German from the first byte — which is the whole point: a page that only
 * becomes German after the browser fetches a translation is not German to a
 * crawler.
 *
 * The surrounding chrome switches to German after hydration via setLanguage.
 * That happens post-render on both server and client, so there is no hydration
 * mismatch — the content that has to rank is already German either way.
 */
export const Route = createFileRoute("/de/themen/$slug")({
  loader: ({ params }) => {
    if (!getTopicDe(params.slug)) throw notFound();
    return null;
  },
  head: ({ params }) => {
    const topic = getTopicDe(params.slug);
    if (!topic)
      return buildSeo({
        path: `/de/themen/${params.slug}`,
        title: "Agentische KI im B2B — Themen | Reality Twin",
        description:
          "Erklärungen zu agentischer KI, Geschäft zwischen KI-Systemen und verifizierter Firmenidentität — jede davon ehrlich darüber, was heute funktioniert.",
        noindex: true,
      });

    const de = `${SITE}/de/themen/${topic.slug}`;
    const en = `${SITE}/topics/${topic.slug}`;
    const seo = buildSeo({
      path: `/de/themen/${topic.slug}`,
      title: topic.metaTitle,
      description: topic.metaDescription,
      type: "article",
    });

    return {
      ...seo,
      links: [
        ...seo.links,
        // Genuine alternates: both URLs exist and each really serves its
        // language. The previous ?lang= alternates did neither.
        { rel: "alternate", hrefLang: "de", href: de },
        { rel: "alternate", hrefLang: "en", href: en },
        { rel: "alternate", hrefLang: "x-default", href: en },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: topic.metaTitle,
            description: topic.metaDescription,
            inLanguage: "de",
            author: { "@type": "Organization", name: "Reality Twin" },
            publisher: { "@type": "Organization", name: "Reality Twin", url: SITE },
            mainEntityOfPage: de,
            about: { "@type": "Thing", name: topic.name },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            inLanguage: "de",
            mainEntity: topic.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Reality Twin", item: SITE },
              { "@type": "ListItem", position: 2, name: "Themen", item: `${SITE}/de/themen` },
              { "@type": "ListItem", position: 3, name: topic.name, item: de },
            ],
          }),
        },
      ],
    };
  },
  component: TopicPageDe,
});

function TopicPageDe() {
  const { slug } = Route.useParams();
  const topic = getTopicDe(slug);

  // A visitor who lands on a German URL wants a German interface. Runs after
  // render, so it changes nothing about the server HTML.
  useEffect(() => {
    void setLanguage("de");
  }, []);

  if (!topic) return null;
  const others = TOPICS_DE.filter((x) => x.slug !== topic.slug);

  return (
    <MarketingLayout>
      <article className="py-14 sm:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <Eyebrow>{topic.name}</Eyebrow>
          <h1 className="mt-4 font-serif text-[36px] sm:text-[56px] leading-[1.03] tracking-[-0.03em]">
            {topic.name}
          </h1>
          <p className="mt-6 text-[17px] leading-[1.6] text-muted-foreground">{topic.heroLead}</p>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-12 space-y-5">
          {topic.thesis.map((p, i) => (
            <p key={i} className="text-[17px] leading-[1.7]">
              {p}
            </p>
          ))}
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            Wie das in der Praxis aussieht
          </h2>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {topic.inPractice.map((s, i) => (
              <div key={i} className="rounded-2xl hairline bg-card p-5">
                <div className="font-medium">{s.title}</div>
                <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Der Abschnitt, der die Seite überprüfbar macht. */}
        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            Was heute stimmt
          </h2>
          <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">
            Diesen Abschnitt gibt es, weil die meisten Seiten in diesem Feld keinen haben. Beide
            Spalten werden gegen die Statusliste des Produkts gepflegt, nicht vom Marketing
            geschrieben.
          </p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl hairline bg-card p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                Funktioniert heute
              </div>
              <ul className="mt-3 space-y-2.5">
                {topic.reality.works.map((w, i) => (
                  <li key={i} className="flex gap-2 text-[14px] leading-[1.55]">
                    <Check
                      className="h-4 w-4 shrink-0 mt-0.5 text-[oklch(0.62_0.14_168)]"
                      strokeWidth={2}
                    />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl hairline bg-cream p-5">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
                Funktioniert nicht — und wir tun nicht so
              </div>
              <ul className="mt-3 space-y-2.5">
                {topic.reality.doesNot.map((d, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-[14px] leading-[1.55] text-muted-foreground"
                  >
                    <Minus className="h-4 w-4 shrink-0 mt-0.5" strokeWidth={2} />
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <Link
            to="/roadmap"
            className="mt-4 inline-flex items-center gap-1.5 text-[14px] hover:opacity-70"
          >
            Status aller Funktionen ansehen
            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
          </Link>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="rounded-2xl hairline bg-card p-6 sm:p-8">
            <h2 className="font-serif text-[24px] sm:text-[28px] tracking-[-0.02em]">
              Prüf es an deiner eigenen Webseite
            </h2>
            <p className="mt-3 text-[15px] leading-[1.6] text-muted-foreground">
              Der kostenlose Twin-Check liest deine Seite so, wie ein KI-Assistent es täte, und sagt
              dir, was er über dich beantworten kann und was nicht. Ohne Konto.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row flex-wrap gap-2">
              <Link
                to="/twin-check"
                className="inline-flex justify-center items-center gap-1.5 rounded-full bg-foreground px-5 py-3 text-[14px] font-medium text-background hover:opacity-90"
              >
                KI-Lesbarkeit prüfen — kostenlos
                <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
              </Link>
              <Link
                to="/auth"
                className="inline-flex justify-center items-center rounded-full hairline bg-background px-5 py-3 text-[14px] font-medium hover:bg-cream transition"
              >
                Twin erstellen
              </Link>
            </div>
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <h2 className="font-serif text-[26px] sm:text-[32px] tracking-[-0.02em]">
            Fragen, die wirklich gestellt werden
          </h2>
          <div className="mt-6 divide-y divide-border hairline rounded-2xl bg-card">
            {topic.faqs.map((f, i) => (
              <div key={i} className="p-5">
                <div className="font-medium">{f.q}</div>
                <p className="mt-2 text-[14px] leading-[1.6] text-muted-foreground">{f.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-12">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
            <MessageSquareQuote className="h-3.5 w-3.5" />
            Diese Seite beantwortet
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {topic.queries.map((q, i) => (
              <span key={i} className="rounded-full hairline bg-card px-3 py-1.5 text-[12.5px]">
                {q}
              </span>
            ))}
          </div>
        </div>

        <div className="mx-auto max-w-3xl px-6 mt-14">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">
            Mehr dazu, wie das funktioniert
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {others.map((o) => (
              <Link
                key={o.slug}
                to="/de/themen/$slug"
                params={{ slug: o.slug }}
                className="rounded-full hairline bg-card px-3.5 py-1.5 text-[13px] hover:bg-cream transition"
              >
                {o.name}
              </Link>
            ))}
          </div>
          <Link
            to="/topics/$slug"
            params={{ slug: topic.slug }}
            className="mt-5 inline-block text-[13px] text-muted-foreground hover:text-foreground"
          >
            Read this page in English →
          </Link>
        </div>
      </article>
    </MarketingLayout>
  );
}
